import {
  Button,
  Image,
  useDisclosure,
  Modal,
  ModalContent,
  Card,
  CardFooter,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ModalBody,
  ModalHeader,
  ButtonGroup,
  Listbox,
  ListboxItem,
  Tooltip,
} from "@heroui/react";
import {
  Link,
  Link as RemixLink,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
} from "@remix-run/react";
import { MdAccountCircle, MdClose, MdOutlineLocalPhone } from "react-icons/md";
import { PiHandbagLight } from "react-icons/pi";
import { FaAward } from "react-icons/fa6";
import { SecondaryNav } from "./SecondaryNav";
import { CategoryHeaderBar } from "./CategoryHeaderBar";
import { navLinks } from "./navLinks";
import logo from "app/assets/logo.png";
import mobileLogo from "app/assets/logo-60x60.png";
// TODO: Create logo-90x90.png or logo-120x120.png for high-DPI displays
// import mobileLogoHighRes from "app/assets/logo-90x90.png"; // or logo-120x120.png
import { FiLogIn, FiMenu } from "react-icons/fi";
import { useState, useCallback, useEffect, forwardRef } from "react";
import { IBanner, IPopup } from "app/api/banner/types";
import allCategory from "app/utils/categories";
import CategoryList from "../CategoryList";
import { fetchBannerApi } from "app/api/banner/banner.api";
import CartComponent from "../Cart/CartComponent";
import DarkModeSwitch from "../DarkModeSwitch";
import { SearchDropdown } from "./SearchDropdown";
import QuotePopover from "../RequestQuote/QuotePopover";
import { isQuoteOpen, quotesList } from "app/atoms/quote.atom";
import { useAtom, useAtomValue } from "jotai";
import { BiCart } from "react-icons/bi";
import { categoryI } from "app/api/category/category.types";
import { cartList } from "app/atoms/cart.atom";
import { isMobile, isTablet } from "react-device-detect";
import { userAtom } from "app/atoms/user.atom";
import QuoteIcon from "../QuoteIcon";
import { useDeviceType } from "app/utils/deviceDetection";

/** Shared badge for quote/cart counts in header - forwardRef for PopoverTrigger compatibility */
const HeaderBadge = forwardRef<HTMLDivElement, { count: number; children: React.ReactNode }>(
  function HeaderBadge({ count, children }, ref) {
    const show = count > 0;
    return (
      <div ref={ref} className="relative inline-flex group">
        {show && (
          <span
            className="absolute -top-0.5 -right-0.5 z-10 min-w-[1.25rem] h-5 px-1 flex items-center justify-center text-xs font-semibold text-primary-foreground bg-primary rounded-full"
            aria-hidden
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
        {children}
      </div>
    );
  },
);

type HeaderT = {
  sidebarOpen: string | boolean | undefined;
  setSidebarOpen: (arg0: boolean) => void;
  categories: categoryI[];
  userId?: string;
};

export function Header({ sidebarOpen, setSidebarOpen, categories }: HeaderT) {
  const user = useAtomValue(userAtom);
  const { isLaptop } = useDeviceType();

  const navigate = useNavigate();
  const navigation = useNavigation();

  const [_isQuoteOpen, setIsQuoteOpen] = useAtom(isQuoteOpen);
  const quoteList = useAtomValue(quotesList);
  const cartsList = useAtomValue(cartList);

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isCartOpen,
    onOpen: onCartOpen,
    onOpenChange: onCartOpenChange,
  } = useDisclosure();
  const {
    isOpen: isAccountOpen,
    onOpen: onAccountOpen,
    onOpenChange: onAccountOpenChange,
  } = useDisclosure();

  const [banner, setBanner] = useState<IBanner | null>(null);
  const [popup, setPopup] = useState<IPopup | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [_categories, setCategories] = useState<categoryI[]>([]);
  const [isScrolled, setIsScrolled] = useState(false);
  /** Defer device-dependent UI until after mount to avoid hydration mismatch (react-device-detect differs server vs client) */
  const [showDesktopQuotePopover, setShowDesktopQuotePopover] = useState(false);

  const location = useLocation();
  // const data = useLoaderData<any>();

  useEffect(() => {
    if (!categories || categories.length === 0) {
      setCategories(allCategory);
    } else {
      setCategories(categories);
    }
  }, [categories, categories?.length]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setShowDesktopQuotePopover(!isMobile || isTablet);
  }, []);

  const handleOpenSidebar = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [setSidebarOpen, sidebarOpen]);

  const SALESCONTACT = "+61 1300664999";

  const handleLogin = () => {
    navigate("/auth/login");
  };

  useEffect(() => {
    fetchBannerApi().then((data) => {
      setBanner(data?.banner);
      setPopup(data?.popupModal);
      if (data?.popupModal?.isActive) {
        onOpen();
      }
    });
  }, [onOpen]);

  return (
    <>
      {/* Fixed dark mode switch on right - big screens only */}
      <div
        className="p-0.5 fixed right-0 top-[3.5rem]  z-[1000] hidden xl:block bg-default-100 hover:bg-default-200 rounded-l-xl border-2 border-r-0   w-fit h-fit border-default-200 overflow-hidden"
        aria-label="Theme toggle"
      >
        <DarkModeSwitch />
      </div>
      {banner && banner.isActive && navigation.state !== "loading" && (
        <div className="bg-primary text-white md:text-xl 4xl:text-2xl px-3 py-2 w-full">
          {banner.message}
        </div>
      )}
      {/* Trust strip - simplified for xl: guarantee near phone */}
      <div className="bg-darkerPrimary hidden lg:flex items-center justify-center gap-4 sm:gap-6 py-2 px-4 text-xs sm:text-sm border-b border-primary/20 dark:border-gray-600/80 dark:bg-slate-800/95">
        <span className="font-medium text-white dark:text-white/95">Free quotes</span>
        <span className="text-white/70 dark:text-gray-400" aria-hidden>
          •
        </span>
        <span className="font-medium text-white dark:text-white/95">Price guarantee</span>
        <span className="text-white/70 dark:text-gray-400" aria-hidden>
          •
        </span>
        <span className="font-medium text-white dark:text-white/95">20+ years in Australia</span>
        <span className="text-white/70 dark:text-gray-400" aria-hidden>
          •
        </span>

        <span className="font-medium text-white dark:text-white/95">
          Fast Delivery Australia Wide
        </span>
        <span className="text-white/70 dark:text-gray-400" aria-hidden>
          •
        </span>
        <div className="flex items-center gap-2 xl:gap-3">
          <RemixLink
            to={`tel:${SALESCONTACT}`}
            className="font-semibold text-white hover:text-lightBlue hover:underline transition-colors dark:text-primary dark:hover:text-primary/90"
            aria-label="Call 1300 664999 for sales"
            title="Australian Operators"
          >
            Ph: 1300 664999
          </RemixLink>
          <RemixLink
            to="/guarantees"
            className="hidden xl:inline-flex items-center gap-1 font-medium text-white hover:text-lightBlue transition-colors dark:text-white/95 dark:hover:text-primary"
            aria-label="Our Guarantees"
          >
            <FaAward className="text-orange shrink-0" aria-hidden />
            Our Guarantees
          </RemixLink>
        </div>
      </div>
      <header
        className={`sticky sm:relative top-0 w-full z-50 h-fit transition-shadow duration-200 ${
          isScrolled ? "shadow-md dark:shadow-black/20" : ""
        } ${location.pathname !== "/" ? "border-b border-white-border dark:border-gray-500" : ""}`}
      >
        <div className="px-2 md:px-6 lg:px-8 xl:px-12 bg-white dark:bg-gray-900">
          <div className={`container mx-auto ${location.pathname !== "/" ? "pb-4" : ""}`}>
            <nav className="py-3 lg:py-4" aria-label="Main header">
              <div className="flex flex-wrap items-center justify-between gap-4 mx-auto">
                <RemixLink
                  to="/"
                  className="hidden xl:block shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                >
                  <Image
                    src={logo}
                    loading="lazy"
                    width={180}
                    height={43}
                    style={{ aspectRatio: "4.15/1" }}
                    removeWrapper
                    alt="Company Logo"
                    radius="none"
                  />
                </RemixLink>

                <div className="flex gap-2 sm:gap-3 items-center justify-between w-full xl:hidden">
                  <div className="flex items-center gap-x-4 shrink-0">
                    <Button
                      // type="button"
                      onPress={handleOpenSidebar}
                      className="outline-none inline-flex items-center justify-center p-2 -ml-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      aria-controls="navbar-sticky"
                      aria-expanded={!!sidebarOpen}
                      isIconOnly
                      color="primary"
                      variant="bordered"
                      aria-label="Toggle navigation menu"
                    >
                      <FiMenu className="text-2xl" aria-hidden />
                    </Button>
                    <RemixLink
                      to="/"
                      className="xl:hidden w-auto max-w-28 sm:max-w-32  lg:max-w-24 md:mr-1"
                      aria-label="Home Link"
                    >
                      <Image
                        src={mobileLogo}
                        alt="Company Logo"
                        width={60}
                        height={60}
                        srcSet={`${mobileLogo} 1x`}
                        sizes="60px"
                        style={{ aspectRatio: "1/1" }}
                        radius="none"
                      />
                    </RemixLink>{" "}
                  </div>
                  <div
                    className="hidden lg:flex flex-1 min-w-0 max-w-md mx-2 lg:mx-4"
                    aria-label="Search"
                  >
                    <SearchDropdown />
                  </div>
                  <div className="xl:hidden space-x-2 flex shrink-0">
                    <Button
                      as={RemixLink}
                      isIconOnly
                      to={`tel:+${SALESCONTACT}`}
                      variant="ghost"
                      aria-label="Sales Contact"
                      className="border-0 rounded inline-flex"
                    >
                      <MdOutlineLocalPhone size={30} className=" " />
                    </Button>
                    {!user || !user._id ? (
                      <Button
                        type="button"
                        onPress={handleLogin}
                        variant="ghost"
                        className="border-0"
                        isIconOnly={true}
                        aria-label="Login Button"
                      >
                        <MdAccountCircle size={30} />
                      </Button>
                    ) : (
                      <Button
                        as={RemixLink}
                        isIconOnly
                        color="primary"
                        to="/account"
                        prefetch="render"
                        size="lg"
                        variant="light"
                        aria-label="Account"
                      >
                        <MdAccountCircle size={30} />
                      </Button>
                    )}

                    <HeaderBadge count={quoteList?.length ?? 0}>
                      <Button
                        as={RemixLink}
                        isIconOnly
                        variant="ghost"
                        to="/quotes"
                        aria-label="Quote Request"
                        className={`${
                          location.pathname === "/quotes" ? "text-primary" : ""
                        } border-0 rounded-full focus-visible:ring-2 focus-visible:ring-primary`}
                      >
                        <QuoteIcon className="w-8 h-8 text-zinc-800 dark:text-white" />
                      </Button>
                    </HeaderBadge>
                    <HeaderBadge count={cartsList?.length ?? 0}>
                      <Button
                        isIconOnly
                        variant="ghost"
                        aria-label="Cart"
                        className="border-0 rounded-full focus-visible:ring-2 focus-visible:ring-primary"
                        onPress={onCartOpen}
                      >
                        <BiCart size={30} />
                      </Button>
                    </HeaderBadge>
                  </div>
                </div>
                <div className="sm:ml-8 flex md:order-2 md:space-x-0 rtl:space-x-reverse">
                  <ul className="hidden xl:flex items-center gap-2">
                    <li>
                      {!user || !user._id ? (
                        <Button
                          type="button"
                          onPress={handleLogin}
                          variant="bordered"
                          startContent={<FiLogIn className="text-primary shrink-0" aria-hidden />}
                          className="border-gray-200 dark:border-gray-600 font-medium text-sm rounded-lg"
                          aria-label="Login"
                        >
                          Login
                        </Button>
                      ) : (
                        <Popover
                          isOpen={isAccountOpen}
                          onOpenChange={onAccountOpenChange}
                          showArrow
                          placement="bottom-end"
                        >
                          <PopoverTrigger>
                            <Button
                              type="button"
                              variant="bordered"
                              size="md"
                              aria-label="Account"
                              startContent={
                                <MdAccountCircle
                                  className="text-lg text-primary shrink-0"
                                  aria-hidden
                                />
                              }
                              className="border-gray-200 dark:border-gray-600 font-medium text-sm rounded-lg"
                              onPress={() => onAccountOpen()}
                            >
                              Account
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="max-w-md p-3">
                            <div className="flex flex-col gap-2">
                              <Listbox aria-label="Actions">
                                <ListboxItem key="user-name">
                                  <span className="text-lg font-semibold">
                                    {user?.firstName || "Account"}
                                  </span>
                                </ListboxItem>
                                <ListboxItem as={Link} to="/edit-account" key="edit-account">
                                  Edit Account
                                </ListboxItem>
                                <ListboxItem as={Link} to="/account" key="all-orders">
                                  All Orders
                                </ListboxItem>
                              </Listbox>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </li>
                    <li>
                      <ButtonGroup>
                        {showDesktopQuotePopover && (
                          <Popover
                            isOpen={_isQuoteOpen}
                            onOpenChange={(open) => {
                              setIsQuoteOpen(open);
                            }}
                            showArrow={true}
                            placement="bottom-end"
                          >
                            <PopoverTrigger>
                              <HeaderBadge count={quoteList?.length ?? 0}>
                                <Button
                                  variant="ghost"
                                  onPress={() => setIsQuoteOpen(true)}
                                  className={`${
                                    location.pathname === "/quotes" ? "dark:bg-primary" : ""
                                  } !rounded-s-md !rounded-e-none border-r-0 font-medium px-3 focus-visible:ring-2 focus-visible:ring-primary`}
                                >
                                  <QuoteIcon className="w-6 h-6 text-zinc-800 dark:text-white" />
                                  Quote Cart
                                </Button>
                                <div
                                  className="absolute left-1/2 -translate-x-1/2 top-full my-2 hidden group-hover:block bg-secondary text-white font-normal rounded-xl px-2.5 py-1 whitespace-nowrap text-sm pointer-events-none"
                                  role="tooltip"
                                >
                                  Quotation Cart
                                </div>
                              </HeaderBadge>
                            </PopoverTrigger>
                            <PopoverContent className="p-1 dark:border-2 border-gray-500">
                              <QuotePopover onToggle={(v) => setIsQuoteOpen(v)} />
                            </PopoverContent>
                          </Popover>
                        )}
                        <div className="relative overflow-y-visible group font-medium">
                          <HeaderBadge count={cartsList?.length ?? 0}>
                            <Button
                              as={RemixLink}
                              to="/cart"
                              variant="ghost"
                              aria-label="View Cart Button"
                              className={`${
                                location.pathname === "/cart" ? "dark:bg-primary" : ""
                              } !rounded-s-none !rounded-e-md font-medium px-3 focus-visible:ring-2 focus-visible:ring-primary`}
                            >
                              <PiHandbagLight className="text-lg xl:text-xl 2xl:text-2xl" />
                              {user?.isCreditAccount ? "Order" : "Buy"}
                            </Button>
                            <div
                              className="z-10 absolute left-1/2 -translate-x-1/2 top-full my-2 hidden group-hover:block bg-secondary text-white font-normal rounded-xl px-2.5 py-1 whitespace-nowrap text-sm pointer-events-none"
                              role="tooltip"
                            >
                              {user?.isCreditAccount ? "Order Cart" : "Buy Now Cart"}
                            </div>
                          </HeaderBadge>
                        </div>
                      </ButtonGroup>
                    </li>
                  </ul>
                </div>
                <div
                  className="hidden xl:flex items-center gap-6 md:order-1 flex-1 min-w-0"
                  aria-label="Main navigation and search"
                >
                  <div
                    className="flex-1 min-w-0 max-w-lg mx-auto overflow-hidden"
                    aria-label="Search"
                  >
                    <SearchDropdown />
                  </div>
                </div>
              </div>
            </nav>
            {location.pathname === "/" ? (
              <div className="container w-full !mt-auto">
                <div className="lg:hidden">
                  {" "}
                  <SecondaryNav uid={user?._id ?? null} />
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center gap-4 flex-wrap w-full mt-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full min-w-0">
                  <div className="flex-1 min-w-0 lg:hidden">
                    <SearchDropdown />
                  </div>
                </div>
              </div>
            )}
            <CategoryHeaderBar categories={_categories} />
          </div>
        </div>
      </header>
      {popup && (
        <Modal
          closeButton={true}
          classNames={{ closeButton: "z-10 border-2 font-bold bg-zinc-100" }}
          isOpen={isOpen}
          placement={popup?.position ?? "auto"}
          onOpenChange={onOpenChange}
        >
          <ModalContent>
            <Card isFooterBlurred className="z-0 w-full h-full col-span-12 sm:col-span-7 p-0">
              <Image
                removeWrapper
                alt={popup.message || "Popup image"}
                className="z-0 w-full h-full object-cover"
                src={popup.image}
                loading="lazy"
              />
              {popup?.message && (
                <CardFooter className="absolute bg-black/40 bottom-0 z-10 border-t-1 border-default-600 dark:border-default-100">
                  <div className="flex flex-grow gap-2 items-center">
                    <div className="flex flex-col">
                      <p className="text-tiny text-white/60">{popup.message}</p>
                    </div>
                  </div>
                  <Button as={RemixLink} to={popup.urlLink} radius="full" size="sm">
                    Continue
                  </Button>
                </CardFooter>
              )}
            </Card>
          </ModalContent>
        </Modal>
      )}

      <Modal
        placement="top"
        isOpen={isCartOpen}
        scrollBehavior="inside"
        onOpenChange={onCartOpenChange}
        className="mt-20 mx-2 xl:hidden max-h-[90vh] !max-w-[90vw] lg:max-w-[70vw]"
        radius="none"
      >
        <ModalContent className="border border-foreground-500">
          <ModalHeader>
            <div className="mb-5">
              <h1 className="text-2xl capitalize font-bold text-center my-4">
                {user?.isCreditAccount ? "Order Cart" : "Buy Now Cart"}
              </h1>
              {user && user?._id && (
                <p className="text-center text-wrap text-sm sm:text-base">
                  Also includes any products you added when last logged in.{" "}
                  <br className="hidden sm:block" /> To remove any products, click the
                  <span>
                    <MdClose size={20} className="text-danger inline-block" />.
                  </span>
                </p>
              )}
            </div>
          </ModalHeader>
          <ModalBody className="px-2">
            <CartComponent onCartOpenChange={onCartOpenChange} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
